'use client';

import { useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import React, { startTransition } from 'react';

import { AvailableLocales, Locale } from '@cineamo/ui/src/constants/locale-constants';

import { getLanguageCodeFromLocale, getLocationNameFromLocale } from '@cineamo/ui/src/helpers/locale-helper';
import { setPreferredLocale } from '@cineamo/ui/src/helpers/preferred-language-helper';

import BasicDropdown, { BasicDropdownOption } from '@cineamo/ui/src/dropdown/BasicDropdown';
import { IconGlobe16 } from '@cineamo/ui/src/icons/ic-globe';

import { usePathname, useRouter } from '@/i18n/routing';

const LanguageDropdown: React.FC = () => {
    const router = useRouter();
    const currentPath = usePathname();
    const searchParams = useSearchParams();
    const queryParams: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
        queryParams[key] = value;
    });
    const currentLocale = useLocale();

    const languageDropdownOptions: BasicDropdownOption<Locale>[] = AvailableLocales.map((locale) => ({
        value: locale,
        label: `${getLanguageCodeFromLocale(locale).toUpperCase()} - ${getLocationNameFromLocale(locale)}`
    }));

    async function onLanguageChange(selectedLocale: Locale) {
        await setPreferredLocale(selectedLocale);
        const newLocale = getLanguageCodeFromLocale(selectedLocale);
        startTransition(() => {
            router.replace(currentPath, {
                locale: newLocale,
                scroll: false
            });
        });
    }

    return (
        <BasicDropdown
            className="h-38 lg:h-28"
            openingDirection="up"
            height="custom"
            triggerVariant="surface"
            triggerIcon={<IconGlobe16 className="flex-none" />}
            preselectedIndex={languageDropdownOptions.findIndex((it) => it.value.includes(currentLocale))}
            options={languageDropdownOptions}
            onChange={(selectedItem) => {
                onLanguageChange(selectedItem.value).then();
            }}
        />
    );
};

export default LanguageDropdown;
